import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Box, Typography, Button, Tooltip } from "@mui/material";
import {
    KeyboardArrowUp,
    KeyboardArrowDown,
    Bolt,
    OpenInNew,
} from "@mui/icons-material";
import styled from "@emotion/styled";
import { useNavigate } from "react-router-dom";
import BaseWidget from "./BaseWidget";
import { useMultiFetchQueue } from "../hooks/useFetchQueue";
import { requestUserAssignments } from "../redux/sagas/requests/users";
import { getRunningTimerByUser } from "../redux/ducks/time";
import { useHasPermission } from "../utils/permissionUtils";
import RunningTimer from "../components/RunningTimer";
import TimerButton from "../components/TimerButton";
import {
    URGENCY_ORDER,
    URGENCY_META,
    buildStats,
    bucketTasksByUrgency,
    dueShort,
    dueTone,
    isResponsible,
    isTask,
    sortAssignments,
} from "../utils/assignmentUtils";

// ── Workload mini strip ──────────────────────────────────────────────────────

const StatRow = styled(Box)(({ theme }) => ({
    display: "grid",
    gridTemplateColumns: "repeat(4, 1fr)",
    gap: theme.spacing(1),
    marginBottom: theme.spacing(2),
}));

const StatTile = styled(Box, {
    shouldForwardProp: (prop) => prop !== "$tone",
})(({ theme, $tone }) => {
    const toneMap = {
        neutral: theme.palette.text.primary,
        warn: theme.palette.warning.dark,
        error: theme.palette.error.dark,
    };
    return {
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        padding: theme.spacing(0.75, 0.5),
        borderRadius: theme.shape.borderRadius,
        border: `1px solid ${theme.palette.divider}`,
        background: theme.palette.background.paper,
        "& .stat-value": {
            fontSize: theme.typography.size.lg,
            fontWeight: theme.typography.fontWeightSemiBold,
            lineHeight: 1.1,
            color: toneMap[$tone] || toneMap.neutral,
            fontVariantNumeric: "tabular-nums",
        },
        "& .stat-label": {
            fontSize: theme.typography.size.xxs,
            fontWeight: theme.typography.fontWeightMedium,
            textTransform: "uppercase",
            letterSpacing: "0.06em",
            color: theme.palette.text.secondary,
            marginTop: 2,
        },
    };
});

// ── Hero (compact) ───────────────────────────────────────────────────────────

const HeroCard = styled(Box, {
    shouldForwardProp: (prop) => prop !== "$active",
})(({ theme, $active }) => ({
    padding: theme.spacing(1.25, 1.5),
    marginBottom: theme.spacing(2),
    borderRadius: theme.shape.borderRadius,
    border: `1px solid ${theme.palette.divider}`,
    borderLeft: `3px solid ${$active ? theme.palette.primary.main : theme.palette.warning.main}`,
    background: theme.palette.background.paper,
}));

const HeroEyebrow = styled(Box)(({ theme }) => ({
    fontSize: theme.typography.size.xxs,
    fontWeight: theme.typography.fontWeightSemiBold,
    textTransform: "uppercase",
    letterSpacing: "0.1em",
    color: theme.palette.text.secondary,
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(0.5),
    marginBottom: theme.spacing(0.25),
}));

const HeroDot = styled(Box, {
    shouldForwardProp: (prop) => prop !== "$active",
})(({ theme, $active }) => ({
    width: 6,
    height: 6,
    borderRadius: "50%",
    background: $active ? theme.palette.primary.main : theme.palette.warning.main,
}));

const HeroTitle = styled(Typography)(({ theme }) => ({
    fontSize: theme.typography.size.base,
    fontWeight: theme.typography.fontWeightMedium,
    cursor: "pointer",
    "&:hover": { color: theme.palette.primary.main, textDecoration: "underline" },
    lineHeight: 1.3,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
}));

const HeroMeta = styled(Typography)(({ theme }) => ({
    fontSize: theme.typography.size.sm,
    color: theme.palette.text.secondary,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
}));

const HeroFooter = styled(Box)(({ theme }) => ({
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    gap: theme.spacing(0.75),
    marginTop: theme.spacing(0.75),
}));

const RunningClockSmall = styled(Box)(({ theme }) => ({
    fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, "Liberation Mono", monospace',
    fontSize: theme.typography.size.smd,
    color: theme.palette.primary.main,
    fontWeight: theme.typography.fontWeightMedium,
}));

// ── Category section ─────────────────────────────────────────────────────────

const CategoryBlock = styled(Box)(({ theme }) => ({
    marginBottom: theme.spacing(1.5),
}));

const CategoryHeader = styled(Box, {
    shouldForwardProp: (prop) => prop !== "$accent",
})(({ theme, $accent }) => {
    const accentMap = {
        error: theme.palette.error.main,
        warn: theme.palette.warning.main,
        info: theme.palette.primary.main,
        neutral: theme.palette.text.disabled,
    };
    const accentColor = accentMap[$accent] || theme.palette.text.disabled;
    return {
        position: "relative",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        paddingBottom: theme.spacing(0.5),
        paddingLeft: theme.spacing(1),
        marginBottom: theme.spacing(0.5),
        borderBottom: `1px solid ${theme.palette.divider}`,
        cursor: "pointer",
        userSelect: "none",
        "& .accent-error": { color: theme.palette.error.main },
        "& .accent-warn": { color: theme.palette.warning.main },
        "& .accent-info": { color: theme.palette.primary.main },
        "& .accent-neutral": { color: theme.palette.text.secondary },
        "&::before": {
            content: '""',
            position: "absolute",
            left: 0,
            top: "20%",
            bottom: "30%",
            width: 2,
            borderRadius: 1,
            background: accentColor,
        },
    };
});

const CategoryLeft = styled(Box)(({ theme }) => ({
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(0.75),
    "& .MuiSvgIcon-root": { fontSize: theme.typography.size.md, color: theme.palette.text.secondary },
}));

const CategoryLabel = styled(Typography)(({ theme }) => ({
    fontSize: theme.typography.size.xs,
    fontWeight: theme.typography.fontWeightSemiBold,
    textTransform: "uppercase",
    letterSpacing: "0.08em",
    color: theme.palette.text.primary,
}));

const CategoryCount = styled(Typography)(({ theme }) => ({
    fontSize: theme.typography.size.xs,
    color: theme.palette.text.secondary,
    fontVariantNumeric: "tabular-nums",
}));

// ── Row ──────────────────────────────────────────────────────────────────────

const Row = styled(Box, {
    shouldForwardProp: (prop) => prop !== "$rush" && prop !== "$active",
})(({ theme, $rush, $active }) => ({
    display: "grid",
    gridTemplateColumns: "auto 1fr auto",
    alignItems: "center",
    gap: theme.spacing(0.75),
    padding: theme.spacing(0.5, 0.75),
    borderRadius: theme.shape.borderRadius,
    borderLeft: $rush
        ? `2px solid ${theme.palette.error.main}`
        : $active
            ? `2px solid ${theme.palette.primary.main}`
            : "2px solid transparent",
    "&:hover": { background: theme.palette.action.hover },
}));

const PriorityCol = styled(Box)({
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: 16,
});

const PriorityStack = styled(Box)({
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    lineHeight: 0,
    "& .MuiSvgIcon-root + .MuiSvgIcon-root": { marginTop: -8 },
});

const NameCol = styled(Box)({ minWidth: 0 });

const RowName = styled(Typography)(({ theme }) => ({
    fontSize: theme.typography.size.smd,
    fontWeight: theme.typography.fontWeightMedium,
    cursor: "pointer",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    "&:hover": { color: theme.palette.primary.main, textDecoration: "underline" },
}));

const RowMeta = styled(Typography)(({ theme }) => ({
    fontSize: theme.typography.size.xs,
    color: theme.palette.text.secondary,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
}));

const ActionCol = styled(Box)(({ theme }) => ({
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(0.25),
}));

const RushIcon = styled(Bolt)(({ theme }) => ({
    fontSize: theme.typography.size.sm,
    color: theme.palette.error.main,
    flexShrink: 0,
}));

const EmptyState = styled(Box)(({ theme }) => ({
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    padding: theme.spacing(3, 1),
    color: theme.palette.text.secondary,
    gap: theme.spacing(0.5),
}));

const WatchlistRow = styled(Box)(({ theme }) => ({
    marginTop: theme.spacing(1),
    paddingTop: theme.spacing(1),
    borderTop: `1px solid ${theme.palette.divider}`,
}));

const WatchlistLink = styled(Typography)(({ theme }) => ({
    fontSize: theme.typography.size.sm,
    color: theme.palette.text.secondary,
    cursor: "pointer",
    "&:hover": { color: theme.palette.primary.main, textDecoration: "underline" },
}));

const ViewAllRow = styled(Box)(({ theme }) => ({
    display: "flex",
    justifyContent: "flex-end",
    marginTop: theme.spacing(1),
}));

const PriorityDot = styled(Box)(({ theme }) => ({
    width: 4,
    height: 4,
    borderRadius: "50%",
    backgroundColor: theme.palette.text.disabled,
}));

const NameRow = styled(Box)(({ theme }) => ({
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(0.5),
}));

const DueText = styled("span", {
    shouldForwardProp: (prop) => prop !== "$tone",
})(({ theme, $tone }) => ({
    fontSize: "inherit",
    color: $tone === "late" ? theme.palette.error.main : $tone === "today" ? theme.palette.warning.dark : "inherit",
    fontWeight: $tone === "late" || $tone === "today" ? theme.typography.fontWeightMedium : theme.typography.fontWeightRegular,
}));

const chevronStyle = ({ theme }) => ({
    fontSize: theme.typography.size.base,
    color: theme.palette.text.secondary,
});
const ChevronDown = styled(KeyboardArrowDown)(chevronStyle);
const ChevronUp = styled(KeyboardArrowUp)(chevronStyle);

const ViewAllButton = styled(Button)(({ theme }) => ({
    textTransform: "none",
    fontSize: theme.typography.size.sm,
}));

const ViewAllIcon = styled(OpenInNew)(({ theme }) => ({
    fontSize: `${theme.typography.size.md} !important`,
}));

const PriorityIcon = ({ priority }) => {
    switch (priority) {
        case "Highest":
            return (
                <PriorityStack>
                    <KeyboardArrowUp color="error" fontSize="small" />
                    <KeyboardArrowUp color="error" fontSize="small" />
                </PriorityStack>
            );
        case "High":
            return <KeyboardArrowUp color="warning" fontSize="small" />;
        case "Low":
            return <KeyboardArrowDown color="primary" fontSize="small" />;
        case "Lowest":
            return (
                <PriorityStack>
                    <KeyboardArrowDown color="primary" fontSize="small" />
                    <KeyboardArrowDown color="primary" fontSize="small" />
                </PriorityStack>
            );
        default:
            return <PriorityDot />;
    }
};

// ── Component ────────────────────────────────────────────────────────────────

const MyAssignmentsWidget = ({ widgetId, config = {}, onRemove }) => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const hasPermission = useHasPermission();
    const userId = useSelector((state) => state.auth.userId);
    const timeTracking = useSelector((state) => state.ui.timeTracking);
    const canListTimers = hasPermission("listTimers");

    const [collapsed, setCollapsed] = useState({ upcoming: true });
    const [activeTimer, setActiveTimer] = useState({});

    useEffect(() => {
        if (userId) {
            dispatch(getRunningTimerByUser(userId, (data) => setActiveTimer(data || {})));
        }
    }, [dispatch, userId, timeTracking]);

    const fetchConfigs = useMemo(
        () => [
            {
                apiCall: () => requestUserAssignments(),
                cacheKey: `user-assignments-${userId}`,
                enabled: !!userId,
                transform: (response) => response,
            },
        ],
        [userId]
    );

    const { results, isLoading, error } = useMultiFetchQueue(widgetId, fetchConfigs);
    const assignments = useMemo(() => results[0]?.data || [], [results]);

    const sorted = useMemo(
        () => sortAssignments(assignments.filter((a) => isTask(a) && isResponsible(a))),
        [assignments]
    );
    const stats = useMemo(() => buildStats(sorted), [sorted]);
    const watchlist = useMemo(() => {
        const all = Array.isArray(assignments) ? assignments : [];
        return {
            assigned: all.filter((a) => !isResponsible(a)).length,
            projects: all.filter((a) => !isTask(a) && isResponsible(a)).length,
        };
    }, [assignments]);
    const grouped = useMemo(() => bucketTasksByUrgency(sorted), [sorted]);

    const isActiveTimerOn = (id) => !!(activeTimer && activeTimer.parentId === id);

    const handleNavigate = (a) => {
        const slug = a?.account?.slug;
        if (a.type === "Client") navigate(`/client/${slug}`);
        else if (a.type === "Account") navigate(`/account/${slug}`);
        else if (a.type === "Project") navigate(`/account/${slug}/project/${a._id}`);
        else if (a.type === "Task") navigate(`/account/${slug}/project/${a.project?._id}`);
    };

    const watchlistLabel = () => {
        const parts = [];
        if (watchlist.assigned > 0) parts.push(`${watchlist.assigned} assigned`);
        if (watchlist.projects > 0) {
            parts.push(`${watchlist.projects} project${watchlist.projects === 1 ? "" : "s"}`);
        }
        return parts.join(" · ");
    };

    const renderHero = () => {
        const top = sorted[0];
        if (!top) return null;
        const active = isActiveTimerOn(top._id);
        const tone = dueTone(top.dueDate);
        const dueText = dueShort(top.dueDate);
        return (
            <HeroCard $active={active}>
                <HeroEyebrow>
                    <HeroDot $active={active} />
                    {active ? "Working now" : "Up next"}
                </HeroEyebrow>
                <HeroTitle onClick={() => handleNavigate(top)} title={top.name}>
                    {top.name}
                </HeroTitle>
                <HeroMeta>
                    {top.project?.name || top.account?.name || "Task"}
                    {top.project?.name && top.account?.name ? ` · ${top.account.name}` : ""}
                    {dueText
                        ? ` · `
                        : ""}
                    {dueText && <DueText $tone={tone}>{dueText}</DueText>}
                </HeroMeta>
                <HeroFooter>
                    {active && (
                        <RunningClockSmall>
                            <RunningTimer startTime={activeTimer.startedAt} />
                        </RunningClockSmall>
                    )}
                    {(canListTimers || top.responsible === userId) && (
                        <TimerButton
                            parentId={top._id}
                            parentType={top.type}
                            runningTimerId={active ? activeTimer._id : null}
                        />
                    )}
                </HeroFooter>
            </HeroCard>
        );
    };

    const renderRow = (a) => {
        const active = isActiveTimerOn(a._id);
        const tone = dueTone(a.dueDate);
        const dueText = dueShort(a.dueDate);
        return (
            <Row key={a._id} $rush={a.rush} $active={active}>
                <PriorityCol>
                    <PriorityIcon priority={a.priority} />
                </PriorityCol>
                <NameCol>
                    <NameRow>
                        {a.rush && <RushIcon titleAccess="Rush" />}
                        <RowName onClick={() => handleNavigate(a)} title={a.name}>
                            {a.name}
                        </RowName>
                    </NameRow>
                    <RowMeta>
                        {a.account?.name || ""}
                        {dueText ? " · " : ""}
                        {dueText && <DueText $tone={tone}>{dueText}</DueText>}
                    </RowMeta>
                </NameCol>
                <ActionCol>
                    {(canListTimers || a.responsible === userId) && (
                        <TimerButton
                            parentId={a._id}
                            parentType={a.type}
                            runningTimerId={active ? activeTimer._id : null}
                        />
                    )}
                </ActionCol>
            </Row>
        );
    };

    const renderCategory = (key) => {
        const items = grouped[key];
        if (!items || items.length === 0) return null;
        // Hide the hero's first item from its own category list (avoid duplication).
        const filtered = items.filter((a) => sorted[0] && a._id !== sorted[0]._id);
        if (filtered.length === 0) return null;
        const isCollapsed = !!collapsed[key];
        const meta = URGENCY_META[key];
        const TypeIcon = meta.icon;
        const accentClass = `accent-${meta.accent}`;
        return (
            <CategoryBlock key={key}>
                <CategoryHeader
                    $accent={meta.accent}
                    onClick={() => setCollapsed((p) => ({ ...p, [key]: !p[key] }))}
                    role="button"
                    tabIndex={0}
                    aria-expanded={!isCollapsed}
                >
                    <CategoryLeft>
                        <TypeIcon className={accentClass} />
                        <CategoryLabel>{meta.shortLabel}</CategoryLabel>
                        <CategoryCount>· {filtered.length}</CategoryCount>
                    </CategoryLeft>
                    {isCollapsed ? <ChevronDown /> : <ChevronUp />}
                </CategoryHeader>
                {!isCollapsed && <Box>{filtered.map(renderRow)}</Box>}
            </CategoryBlock>
        );
    };

    return (
        <BaseWidget
            title="My Assignments"
            widgetId={widgetId}
            onRemove={onRemove}
            isLoading={isLoading}
            error={error}
        >
            {sorted.length === 0 && !isLoading ? (
                <EmptyState>
                    <Typography variant="body2">No tasks on you</Typography>
                    <Typography variant="caption">Nothing needs your hands right now.</Typography>
                    {watchlistLabel() && (
                        <WatchlistLink onClick={() => navigate("/assignments")}>
                            {watchlistLabel()} to keep an eye on
                        </WatchlistLink>
                    )}
                </EmptyState>
            ) : (
                <>
                    <StatRow>
                        <Tooltip title="Open tasks you are responsible for">
                            <StatTile $tone="neutral">
                                <Typography className="stat-value">{stats.tasks}</Typography>
                                <Typography className="stat-label">On you</Typography>
                            </StatTile>
                        </Tooltip>
                        <Tooltip title="Tasks on you that are past their due date">
                            <StatTile $tone={stats.overdue > 0 ? "error" : "neutral"}>
                                <Typography className="stat-value">{stats.overdue}</Typography>
                                <Typography className="stat-label">Late</Typography>
                            </StatTile>
                        </Tooltip>
                        <Tooltip title="Tasks on you that are due today">
                            <StatTile $tone={stats.today > 0 ? "warn" : "neutral"}>
                                <Typography className="stat-value">{stats.today}</Typography>
                                <Typography className="stat-label">Today</Typography>
                            </StatTile>
                        </Tooltip>
                        <Tooltip title="Tasks on you marked rush">
                            <StatTile $tone={stats.rush > 0 ? "error" : "neutral"}>
                                <Typography className="stat-value">{stats.rush}</Typography>
                                <Typography className="stat-label">Rush</Typography>
                            </StatTile>
                        </Tooltip>
                    </StatRow>

                    {renderHero()}
                    {URGENCY_ORDER.map(renderCategory)}

                    {watchlistLabel() && (
                        <WatchlistRow>
                            <WatchlistLink onClick={() => navigate("/assignments")}>
                                {watchlistLabel()} to keep an eye on
                            </WatchlistLink>
                        </WatchlistRow>
                    )}

                    <ViewAllRow>
                        <ViewAllButton
                            size="small"
                            endIcon={<ViewAllIcon />}
                            onClick={() => navigate("/assignments")}
                        >
                            View all
                        </ViewAllButton>
                    </ViewAllRow>
                </>
            )}
        </BaseWidget>
    );
};

export default MyAssignmentsWidget;
